// https://github.com/reactjs/react-router/blob/master/docs/guides/ServerRendering.md
// https://github.com/reactjs/react-router-redux/blob/master/examples/server/client.js
import P from 'promise-polyfill';
import assign from 'lodash/assign';

if (!window.Promise) {
  window.Promise = P;
}

if (!Object.assign) {
  Object.assign = assign;
}

import 'lazysizes';
import React from 'react';
import { hydrate, render } from 'react-dom';
import createHistory from 'history/createBrowserHistory';
import matchRoutes from 'react-router-config/matchRoutes';
import scriptJS from 'scriptjs';
import get from 'lodash/get';
import Cookies from 'js-cookie';

import Root from '../views/containers/root_container';
import '../utils/patch_react';

// eslint-disable-next-line import/extensions
// import 'autotrack/autotrack.js';
import './utils/global_util';
import configureStore from '../redux/store/store';
import getRoutesWithStore from '../react_router/react_router';
import initialize from './utils/initializer_util';
import initialLoadActionCreator from '../redux/action_creators/initial_load_action_creator';
import log from './services/logger_service';
import cachingService from './services/caching_service';
import polyfillUtil from './utils/picture_polyfill_util';

// eslint-disable-next-line no-underscore-dangle
window.__forceSmoothScrollPolyfill__ = true;
require('smoothscroll-polyfill').polyfill();

initialize().catch(function logError(err) {
  log.error(err, 'Caught error in initialize.');
});

function bootReact() {
  const browserHistory = createHistory();

  const originalHash = browserHistory.location.hash;

  browserHistory.location.hash = '';

  // get json here
  let bootstrappedConfig = false;
  let env = false;

  try {
    bootstrappedConfig = window.appState;
    env = get(bootstrappedConfig, 'config.env');
    if (env !== 'development') {
      const criticalCss = document.querySelector('.critical-css');
      bootstrappedConfig.config.criticalCss = criticalCss.innerHTML;
    }
    cachingService.initialize(bootstrappedConfig.config.cacheEnabled);
    log.initialize(bootstrappedConfig); // TODO: send, site etc for errors
    browserHistory.location.key = bootstrappedConfig.routing.location.key;
  } catch (error) {
    log.fatal(error, 'Fatal error parsing bootstrap json.');
  }

  if (!bootstrappedConfig) {
    bootstrappedConfig = {}; // TODO: default bootstrap to handle errors
  }
  const store = configureStore(browserHistory, bootstrappedConfig, env);

  const routesToMatch = getRoutesWithStore(store);

  // eslint-disable-next-line no-restricted-globals
  const preloadChunks = matchRoutes(routesToMatch, location.pathname).reduce(
    function matchReduce(list, { route }) {
      if (route.preloadChunk) {
        list.push(route.preloadChunk);
      }
      return list;
    },
    []
  );

  polyfillUtil();

  browserHistory.listen((location /* , action */) => {
    const url = `${location.pathname}`;
    const branch = matchRoutes(routesToMatch, url);
    branch.map(function matchMap({ route, match }) {
      if (route.preloadChunk) {
        return route.preloadChunk().then(() => {
          if (route.loadData) {
            // set gdpr related value for gpt to false to force it to reset it again on next page view
            if (get(window, 'fc_ads.calledNonPersonalize')) {
              window.fc_ads.calledNonPersonalize = false;
            }
            if (!route.autoScrollOff) {
              window.scrollTo(0, 0);
            }

            if (window.localStorage.needToSetDailyCookie) {
              Cookies.set('dailycookie', 'true', { expires: 1 });
              window.localStorage.removeItem('needToSetDailyCookie');
            }

            return route.loadData(match);
          }
          return Promise.resolve(null);
        });
      }
      if (route.loadData) {
        // set gdpr related value for gpt to false to force it to reset it again on next page view
        if (get(window, 'fc_ads.calledNonPersonalize')) {
          window.fc_ads.calledNonPersonalize = false;
        }
        if (!route.autoScrollOff) {
          window.scrollTo(0, 0);
        }

        if (window.localStorage.needToSetDailyCookie) {
          Cookies.set('dailycookie', 'true', { expires: 1 });
          window.localStorage.removeItem('needToSetDailyCookie');
        }

        return route.loadData(match);
      }
      return Promise.resolve(null);
    });

    if (window.googletag) {
      window.googletag.pubads().updateCorrelator();
    }
  });

  function renderedApp() {
    store.dispatch(initialLoadActionCreator());
    browserHistory.location.hash = originalHash;
    scriptJS(
      'https://cdnjs.cloudflare.com/ajax/libs/autotrack/2.2.0/autotrack.js'
    );
    document.documentElement.className +=
      document.documentElement.className === '' ? 'hydrated' : ' hydrated';
  }

  if (!preloadChunks.length) {
    hydrate(
      <Root store={store} history={browserHistory} />,
      window.document,
      renderedApp
    );
  } else {
    Promise.all(preloadChunks.map(chunk => chunk())).then(() => {
      hydrate(
        <Root store={store} history={browserHistory} />,
        window.document,
        renderedApp
      );
    });
  }

  if (module.hot) {
    module.hot.accept(
      ['../react_router/react_router', '../views/containers/root_container'],
      () => {
        // eslint-disable-next-line global-require
        const HotLoadRoot = require('../views/containers/root_container')
          .default;
        render(
          <HotLoadRoot store={store} history={browserHistory} />,
          window.document
        );
      }
    );
  }
}

// TODO: add image polyfill util here
if (!window.Map || !window.Set || !window.requestAnimationFrame) {
  scriptJS(
    [
      'https://cdnjs.cloudflare.com/ajax/libs/babel-polyfill/6.26.0/polyfill.min.js',
      'https://cdnjs.cloudflare.com/ajax/libs/dom4/1.8.3/dom4.js'
    ],
    bootReact
  );
} else {
  bootReact();
}
